import { injectable, inject } from 'inversify';
import { Message, Field, Type } from "protobufjs/light";
import { Result, ok } from '@xlearn/common'
import { reportNonFatalError } from '@/app/error-handling';
import * as SerializationApi from '@/app/state-serialization/api'
import { ProtobufSerializer } from '@/app/state-serialization/infrastructure';
import * as Api from '@/app/state-serialization/api'
import { InteractionTrackingState, InteractionStatus2 } from '../common'

@injectable()
export class InteractionStateProtobufSerializer implements Api.StateSerializationPlugin<InteractionTrackingState[]> {
  key = { id: 5, name: { ns: 'suspend-data', name: 'interactions' }, format: 'protobuf' }

  constructor(
    @inject(SerializationApi.TypedStateSerializerSymbol)
    private serializer: SerializationApi.TypedStateSerializer) {
  }

  serialize(data: InteractionTrackingState[]): Result<Uint8Array, Api.SerializerError> {

    let interactionMessages = []
    for (let interaction of data) {

      const stateResult = this.serializeState(interaction.type, interaction.state)
      if (!stateResult.success) {
        // show message and skip this interaction
        this.displaySerializationError(interaction.type, stateResult.error)
        continue
      }

      const message = new InteractionStateProtoMessage({
        id: interaction.id,
        serializerId: stateResult.value.serializerId,
        status: mapStatus(interaction.status),
        progress: interaction.progress,
        state: stateResult.value.buffer
      })

      interactionMessages.push(message)
    }

    const message = new InteractionStatesProtoMessage({
      interactions: interactionMessages
    })

    return ProtobufSerializer.serialize<InteractionStatesProtoMessage>(message, InteractionStatesProtoMessage)
    
    function mapStatus(input: InteractionStatus2): StatusProtoEnum {
      switch (input) {
        case 'not-attempted':
          return StatusProtoEnum.notAttempted
        case 'incomplete':
          return StatusProtoEnum.incomplete
        case 'completed':
          return StatusProtoEnum.completed
      }
    }
  }

  deserialize(content: Uint8Array): Result<InteractionTrackingState[], Api.DeserializerError> {

    const containerResult = ProtobufSerializer.deserialize<InteractionStatesProtoMessage>(content,
                                                                                     InteractionStatesProtoMessage)
    if (!containerResult.success) {
      return containerResult
    }

    let interactions = []
    for (let interactionMessage of containerResult.value.interactions) {
      const contentResult = this.deserializeState(interactionMessage.serializerId, interactionMessage.state)
      if (!contentResult.success) {
        // show message and skip this interaction, we don't know type yet
        this.displaySerializationError('unknown', contentResult.error)
        continue
      }

      const serializerName = contentResult.value.serializerName
      const interactionType = serializerName.name

      const interaction: InteractionTrackingState = {
        id: interactionMessage.id,
        type: interactionType,
        status: mapStatus(interactionMessage.status),
        progress: interactionMessage.progress,
        state: contentResult.value.content
      }

      interactions.push(interaction)
    }

    return ok(interactions)

    function mapStatus(input: StatusProtoEnum): InteractionStatus2 {
      switch (input) {
        case StatusProtoEnum.notAttempted:
          return 'not-attempted'
        case StatusProtoEnum.incomplete:
          return 'incomplete'
        case StatusProtoEnum.completed:
          return 'completed'
      }
    }
  }

  private serializeState(type: string, state: unknown) {
    return this.serializer.serialize(this.serializerName(type), 'protobuf', state)
  }

  private deserializeState(serializerId: number, buffer: Uint8Array) {
    return this.serializer.deserialize(serializerId, 'protobuf', buffer);
  }

  private displaySerializationError(type: string | 'unknown',
                                    error: SerializationApi.TypedStateSerializeError | SerializationApi.TypedStateDeserializeError) {
    switch (error.code) {
      case 'serializer-not-found':
        const name = error.details
        reportNonFatalError(`interaction 'protobuf' serializer not found with name '${JSON.stringify(name)}', ` +
              `interaction won't be included`)
        break
      case 'serialize-error':
        reportNonFatalError(`interaction 'protobuf' serialization error for type '${type}', ` +
              `interaction won't be included in saved data`, error.details)
      case 'deserialize-error':
        reportNonFatalError(`interaction 'protobuf' deserialization error for type '${type}', ` +
              `interaction won't be included in loaded data`, error.details)
        break
    }
  }

  private serializerName(type: string) {
    return SerializationApi.stateSerializerName('interaction-suspend-data', type)
  }
}

enum StatusProtoEnum {
  notAttempted = 0,
  incomplete = 1,
  completed = 2
}

@Type.d('InteractionStateProtoMessage')
class InteractionStateProtoMessage extends Message<InteractionStateProtoMessage> {

  @Field.d(1, 'string', "optional", '')
  id: string

  @Field.d(2, 'uint32', 'optional')
  serializerId: number

  @Field.d(3, StatusProtoEnum, "optional", StatusProtoEnum.notAttempted)
  status: StatusProtoEnum;

  @Field.d(4, 'float', "optional", 0)
  progress: number

  @Field.d(5, 'bytes', 'optional', [])
  state: Uint8Array
}

@Type.d('InteractionStatesProtoMessage')
class InteractionStatesProtoMessage extends Message<InteractionStatesProtoMessage> {

  @Field.d(1, 'uint32', 'optional', 1)
  public schemaVersion: number

  @Field.d(2, InteractionStateProtoMessage, 'repeated')
  public interactions: InteractionStateProtoMessage[]
}

